"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateGreeksForPosition = exports.getGreeks = exports.calculateOptionGreeks = void 0;
const optionPricer_1 = require("../optionPricer");
const types_1 = require("../types");
const calculationUtils_1 = require("./calculationUtils");
class NormalDist {
    static cdf(x) {
        return 0.5 * (1 + this._phi(x / Math.sqrt(2)));
    }
    static pdf(x) {
        const denom = Math.sqrt(Math.PI * 2);
        const numer = Math.exp(-0.5 * Math.pow(x, 2));
        return numer / denom;
    }
    static erf(x) {
        var cof = [
            -1.3026537197817094, 6.4196979235649026e-1, 1.9476473204185836e-2,
            -9.561514786808631e-3, -9.46595344482036e-4, 3.66839497852761e-4,
            4.2523324806907e-5, -2.0278578112534e-5, -1.624290004647e-6,
            1.30365583558e-6, 1.5626441722e-8, -8.5238095915e-8, 6.529054439e-9,
            5.059343495e-9, -9.91364156e-10, -2.27365122e-10, 9.6467911e-11,
            2.394038e-12, -6.886027e-12, 8.94487e-13, 3.13092e-13, -1.12708e-13,
            3.81e-16, 7.106e-15, -1.523e-15, -9.4e-17, 1.21e-16, -2.8e-17,
        ];
        var j = cof.length - 1;
        var isneg = false;
        var d = 0;
        var dd = 0;
        var t, ty, tmp, res;
        if (x < 0) {
            x = -x;
            isneg = true;
        }
        t = 2 / (2 + x);
        ty = 4 * t - 2;
        for (; j > 0; j--) {
            tmp = d;
            d = ty * d - dd + cof[j];
            dd = tmp;
        }
        res = t * Math.exp(-x * x + 0.5 * (cof[0] + ty * d) - dd);
        return isneg ? res - 1 : 1 - res;
    }
    static erfc(x) {
        return 1 - this.erf(x);
    }
    static _phi(x) {
        // save the sign of x
        const sign = x >= 0 ? 1 : -1;
        x = Math.abs(x);
        // z-scores
        const a1 = 0.254829592;
        const a2 = -0.284496736;
        const a3 = 1.421413741;
        const a4 = -1.453152027;
        const a5 = 1.061405429;
        const p = 0.3275911;
        // A&S formula 7.1.26
        const t = 1.0 / (1.0 + p * x);
        const y = 1.0 -
            ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
        return sign * y; // erf(-x) = -erf(x);
    }
}
const ONE_DAY_IN_YEARS = 0.002739726;
const phi = (x) => {
    return 0.5 * NormalDist.erfc(-x * 0.7071067811865475);
};
const calculateOptionGreeks = (optionType, Spot, K, t, r, v) => {
    const sqrt_t = Math.sqrt(t);
    const vt = v * sqrt_t;
    const d1 = (Math.log(Spot / K) + (r + v * v * 0.5) * t) / vt;
    const d2 = d1 - vt;
    const std_d1 = NormalDist.cdf(d1);
    const exp_r_t = Math.exp(-r * t);
    const gamma = std_d1 / (Spot * vt);
    let vega = 0.01 * Spot * sqrt_t * NormalDist.pdf(d1);
    let delta = 0;
    let rho = 0;
    let thetaYearly = -((Spot * v * NormalDist.pdf(d1)) / (2 * sqrt_t));
    const thetaDaily = thetaYearly / 365;
    let theta = t > ONE_DAY_IN_YEARS ? thetaDaily : thetaDaily * (t / ONE_DAY_IN_YEARS);
    if (optionType === types_1.OptionType.CALL) {
        const phi_d2 = phi(d2);
        delta = phi(d1);
        rho = K * t * exp_r_t * phi_d2 * 0.01;
    }
    else {
        const phi_d2 = phi(-d2);
        delta = phi(d1) - 1;
        rho = -K * t * exp_r_t * phi_d2 * 0.01;
    }
    return { delta, gamma, theta, rho, vega };
};
exports.calculateOptionGreeks = calculateOptionGreeks;
/**
 *
 * @param amount
 * @param positionType
 * @param isAllCurrencies
 * @param instrumentName
 * @param ticker
 * @param calculationTimestamp - Timestamp that we are simulating.
 * @param timestampAtCalculation - Timestamp when we are doing the calculation.
 * @returns
 */
const getGreeks = (amount, positionType, isAllCurrencies, instrumentName, ticker, calculationTimestamp, timestampAtCalculation) => {
    if (positionType === types_1.PositionType.Option) {
        const { strike, optionType } = (0, calculationUtils_1.getOptionProperties)(ticker, timestampAtCalculation, calculationTimestamp, instrumentName);
        const expirationTimestamp = (0, calculationUtils_1.getExpiryTimestampFromInstrumentString)(instrumentName);
        const timeToExpiry = (0, calculationUtils_1.getTimeInYearsBetweenDates)(timestampAtCalculation, expirationTimestamp);
        const { delta, gamma, theta, rho, vega } = (0, exports.calculateOptionGreeks)(optionType, ticker.underlyingPrice, strike, timeToExpiry, 0, ticker.volatility / 100);
        const gammaFinal = isAllCurrencies
            ? 0.5 * gamma * Math.pow((0.01 * ticker.underlyingPrice), 2) * amount
            : gamma * amount;
        return {
            delta: delta * amount,
            gamma: gammaFinal,
            theta: theta * amount,
            rho: rho * amount,
            vega: vega * amount,
            netDelta: (delta - ticker.markPrice) * amount,
        };
    }
    else {
        return {
            gamma: 0,
            vega: 0,
            rho: 0,
            theta: 0,
            delta: amount / ticker.markPrice,
            netDelta: amount / ticker.markPrice,
        };
    }
};
exports.getGreeks = getGreeks;
const calculateGreeksForPosition = (position, ticker, calculationTimestamp) => {
    const instrumentType = (0, calculationUtils_1.getInstrumentType)(position.instrument);
    if (instrumentType === types_1.PositionType.Future ||
        instrumentType === types_1.PositionType.LinearPerpetual) {
        return {
            delta: position.amountCurrency,
            gamma: 0,
            netDelta: 0,
            rho: 0,
            theta: 0,
            vega: 0,
        };
    }
    const { optionType, strike } = (0, calculationUtils_1.getOptionInstrumentProperties)(position.instrument);
    const T = (0, calculationUtils_1.getPositionTimeToExpiryFromInstrumentString)(position.instrument, calculationTimestamp);
    const bs = (0, optionPricer_1.black76)(ticker.underlyingPrice, strike, ticker.volatility / 100, T, 0);
    const delta = optionType === types_1.OptionType.CALL
        ? bs.deltaCall * position.amount
        : bs.deltaPut * position.amount;
    return {
        delta,
        gamma: bs.gamma,
        netDelta: (delta - ticker.markPrice) * position.amount,
        rho: optionType === types_1.OptionType.CALL ? bs.rhoCall : bs.rhoPut,
        theta: optionType === types_1.OptionType.CALL ? bs.thetaCall : bs.thetaPut,
        vega: bs.vega,
    };
};
exports.calculateGreeksForPosition = calculateGreeksForPosition;
